import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import GraderDashboardLayout from "./GraderDashboardLayout";

function GraderDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [graderUid, setGraderUid] = useState(null);
  const [roleChecked, setRoleChecked] = useState(false);
  const navigate = useNavigate();

  // 1) Auth check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/login");
      } else {
        setAuthenticated(true);
        setGraderUid(user.uid);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // 2) Role check (gate this page to graders only)
  useEffect(() => {
    const verifyRole = async () => {
      if (!authenticated || !graderUid) return;
      try {
        const userSnap = await getDoc(doc(db, "users", graderUid));
        const role = userSnap.exists() ? userSnap.data()?.role : null;
        if (role !== "grader") {
          // Not a grader — send them away (or replace with a friendly message)
          navigate("/user-dashboard");
          return;
        }
        setRoleChecked(true);
      } catch (err) {
        console.error("Failed to verify grader role:", err);
        navigate("/login");
      }
    };
    verifyRole();
  }, [authenticated, graderUid, navigate]);

  // Helper to format submitter label
  const getSubmitterLabel = (userData, fallbackUid) => {
    if (!userData) return fallbackUid || "Unknown";
    return (
      userData.username ||
      userData.displayName ||
      userData.name ||
      userData.email ||
      fallbackUid ||
      "Unknown"
    );
  };

  // Build a map of uid -> userData for submitters
  const buildUserMap = async (userIds) => {
    const unique = [...new Set(userIds.filter(Boolean))];
    const pairs = await Promise.all(
      unique.map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          return [uid, snap.exists() ? snap.data() : null];
        } catch {
          return [uid, null];
        }
      })
    );
    return Object.fromEntries(pairs);
  };

  // 3) Load submissions (only after role check passes)
  useEffect(() => {
    if (!authenticated || !graderUid || !roleChecked) return;

    const fetchSubmissions = async () => {
      try {
        const q = query(
          collection(db, "submissions"),
          where("status", "in", ["OPEN", "PENDING"]),
          orderBy("createdAt", "asc") // oldest first
        );
        const snapshot = await getDocs(q);
        const raw = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Enrich with submitter info and formatted time
        const userMap = await buildUserMap(raw.map((s) => s.userId));
        const enriched = raw.map((s) => ({
          ...s,
          submittedBy: getSubmitterLabel(userMap[s.userId], s.userId),
          submittedAtText: s.createdAt?.toDate
            ? s.createdAt.toDate().toLocaleString()
            : "—",
        }));

        setSubmissions(enriched);
      } catch (err) {
        console.error(
          "❌ Firestore read error (likely permissions or index):",
          err.message
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [authenticated, graderUid, roleChecked]);

  const handleReviewClick = (submissionId) => {
    navigate(`/review/${submissionId}`); // matches /review/:submissionId
  };

  if (!authenticated || loading || !roleChecked) return <p>Loading...</p>;

  return (
    <GraderDashboardLayout>
      <div style={{ padding: "20px", maxWidth: "800px", margin: "auto" }}>
        <h2>Grader Dashboard</h2>
        {submissions.length === 0 ? (
          <p>No submissions currently need review.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headerStyle}>Card Name</th>
                <th style={headerStyle}>Submission ID</th>
                <th style={headerStyle}>Submitted By</th>
                <th style={headerStyle}>Submitted At</th>
                <th style={headerStyle}>Status</th>
                <th style={headerStyle}>In Progress By</th>
                <th style={headerStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((submission) => (
                <tr key={submission.id}>
                  <td style={cellStyle}>{submission.cardName}</td>
                  <td style={cellStyle}>{submission.id}</td>
                  <td style={cellStyle}>{submission.submittedBy}</td>
                  <td style={cellStyle}>{submission.submittedAtText}</td>
                  <td style={cellStyle}>{submission.status}</td>
                  <td style={cellStyle}>{submission.inProgressBy || "—"}</td>
                  <td style={cellStyle}>
                    <button
                      onClick={() => handleReviewClick(submission.id)}
                      style={buttonStyle}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </GraderDashboardLayout>
  );
}

const headerStyle = {
  textAlign: "left",
  borderBottom: "2px solid #ddd",
  padding: "8px",
};

const cellStyle = {
  padding: "8px",
  borderBottom: "1px solid #eee",
};

const buttonStyle = {
  padding: "6px 12px",
  backgroundColor: "#1976d2",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  fontWeight: "bold",
  cursor: "pointer",
};

export default GraderDashboard;
